import { ProForm } from '@ant-design/pro-components'
import type { ProFormProps } from '@ant-design/pro-form/es/layouts/ProForm'
import { toast } from '@pkg/ui/toast'
import { Typography } from 'antd'
import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { FormAlerts } from '../components/FormAlerts'
import {
  EditableFormContext,
  type EditableFormMeta,
  type EditablePolicy,
  useEditableForm,
} from '../contexts/editableForm'
import { useFormContext } from '../createAppFormHook'
import { mergeFormProps } from '../utils/mergeFormProps'

export type AppProFormProps<T = Record<string, unknown>> = ProFormProps<T> & {
  children?: ReactNode
  editablePolicy?: Partial<EditablePolicy>
  meta?: Partial<EditableFormMeta>
}

const renderRequiredMark: NonNullable<AppProFormProps['requiredMark']> = (label, { required }) => (
  <Typography.Text className={twMerge(required ? 'font-bold' : 'font-normal')}>
    {label}
    {required && (
      <Typography.Text type="danger" aria-hidden>
        *
      </Typography.Text>
    )}
  </Typography.Text>
)

export function AppProForm<T = Record<string, unknown>>(props: AppProFormProps<T>) {
  const form = useFormContext()
  const { editablePolicy, meta, children, className, layout, requiredMark, submitter, ...rest } = props
  const editable = useEditableForm({
    initialPolicy: { forceMode: 'edit', ...editablePolicy },
    initialMeta: meta,
  })
  const merged = mergeFormProps(
    form,
    {
      ...rest,
      layout,
      className: twMerge(layout === 'horizontal' && 'app-pro-form-horizontal', className),
      requiredMark: requiredMark ?? renderRequiredMark,
      submitter: submitter ?? false,
    },
    editable,
    toast,
  )

  return (
    <EditableFormContext value={editable as never}>
      <ProForm {...merged}>
        <FormAlerts />
        {children}
      </ProForm>
    </EditableFormContext>
  )
}
